import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import useAxios from "axios-hooks";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import { ClassroomSelect } from "../../components/input/ClassroomSelect";
import ConfirmationButton from "../../components/button/ConfirmationButton";
import { XCircleIcon } from "@heroicons/react/24/outline";

type FormValues = {
  classrooms: string[];
  title: string;
  startDate?: number;
  endDate?: number;
  walkthroughEnabled?: boolean;
};

export default function AssignmentForm(props: {
  onSuccess: any;
  defaultValues?: any;
}) {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>();

  const [selectedStartDate, setSelectedStartDate] = useState(
    moment().valueOf()
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    moment().startOf("day").valueOf()
  );
  const [selectedClassroomOptions, setSelectedClassroomOptions] = useState(
    undefined as any
  );

  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (props.defaultValues) {
      setValue("title", props.defaultValues?.title || "");

      if (props.defaultValues?.startDate) {
        const start = new Date(parseInt(props.defaultValues.startDate));

        const timestamp = moment(start).startOf("day").valueOf();

        setSelectedStartDate(timestamp);
        setValue("startDate", timestamp);
      }

      if (props.defaultValues?.endDate) {
        const end = new Date(parseInt(props.defaultValues.endDate));
        const timestamp = moment(end).startOf("day").valueOf();
        setSelectedEndDate(timestamp);
        setValue("endDate", timestamp);
      }

      setValue("walkthroughEnabled", props.defaultValues?.walkthroughEnabled);

      setSelectedClassroomOptions(props?.defaultValues?.classrooms);
      setValue(
        "classrooms",
        props?.defaultValues?.classrooms?.map((cls: any) => cls?._id)
      );
    }
  }, [props.defaultValues]);

  const [
    {
      data: createAssignmentData,
      loading: createAssignmentLoading,
      error: createAssignmentError,
    },
    createAssignment,
  ] = useAxios<any, any>(
    {
      url: "/assignments",
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    { manual: true }
  );

  const [
    {
      data: deleteAssigmentData,
      loading: deleteAssigmentLoading,
      error: deleteAssigmentError,
    },
    deleteAssignment,
  ] = useAxios<any, any>(
    {
      url: `/assignments/${params.assignmentId}`,
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    },
    { manual: true }
  );

  const onSubmit = async (data: FormValues) => {
    try {
      const payload = {
        ...data,
        classrooms: params.classroomId ? [params.classroomId] : data.classrooms,
      };

      if (props.defaultValues) {
        await createAssignment({
          url: `/assignments/${params.assignmentId}`,
          data: payload,
          method: "PATCH",
        });
      } else {
        const response = await createAssignment({
          data: payload,
        });
        if (response.status === 201) {
          props.onSuccess(response.data);
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  if (deleteAssigmentLoading) {
    return <div>Deleting assigment</div>;
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      {/* Title */}
      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">Title</span>
        </div>
        <input
          {...register("title", { required: true })}
          className="input input-bordered input-sm"
        />
        {errors.title && (
          <span className="text-error text-sm">Title is required</span>
        )}
      </label>

      <div className="grid grid-cols-2 gap-2">
        {/* Start Date */}
        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">Start Date</span>
          </div>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                selected={
                  selectedStartDate ? new Date(selectedStartDate) : new Date()
                }
                onChange={(date) => {
                  const timestamp = moment(date).startOf("day").valueOf();
                  field.onChange(timestamp);
                  setSelectedStartDate(timestamp);
                }}
                className="bg-base-300 w-full"
                customInput={<input className="input input-sm" />}
              />
            )}
          />
        </label>

        {/* End Date */}
        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">End Date</span>
          </div>
          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                selected={
                  selectedEndDate ? new Date(selectedEndDate) : new Date()
                }
                onChange={(date) => {
                  const timestamp = moment(date).startOf("day").valueOf();
                  field.onChange(timestamp);
                  setSelectedEndDate(timestamp);
                }}
                className="bg-base-300 w-full"
                customInput={<input className="input input-sm" />}
              />
            )}
          />
        </label>
      </div>

      {/* Walkthrough Toggle */}
      <label className="form-control">
        <div>
          <div className="flex items-center gap-2 cursor-pointer ">
            <span className="">Enable Walkthrough</span>
            <input
              type="checkbox"
              {...register("walkthroughEnabled")}
              className="toggle toggle-primary"
            />
          </div>
          <span className="text-xs">
            Enable or disable lab walkthrough for students.
          </span>
        </div>
      </label>

      {/* Classroom select */}
      {!params.classroomId && (
        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">Classrooms</span>
          </div>
          <Controller
            name="classrooms"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <ClassroomSelect
                defaultSelectedOptions={props?.defaultValues?.classrooms?.map(
                  (classroom: any) => {
                    return {
                      _id: classroom?._id,
                      value: classroom?._id,
                      label: classroom?.name,
                    };
                  }
                )}
                value={selectedClassroomOptions}
                onChange={(options: any) => {
                  setSelectedClassroomOptions(selectedClassroomOptions);
                  field.onChange(options);
                }}
                placeholder="Choose a classroom..."
              />
            )}
          />
          {errors.classrooms && (
            <span className="text-error text-sm">Please select classrooms</span>
          )}
        </label>
      )}

      {/* Submit Button */}
      <div className="flex  mt-5">
        <button className="btn btn-sm btn-outline btn-primary" type="submit">
          {props.defaultValues ? "Update" : "Create"} Assignment
        </button>
        {props.defaultValues && (
          <div className="ml-auto">
            <ConfirmationButton
              id={"delete_assignment"}
              buttonText={"DELETE ASSIGNMENT"}
              body={`Are you sure you want to delete assigment ?`}
              title={"Delete Assignment"}
              onAccept={async () => {
                try {
                  const response = await deleteAssignment();
                  if (response.data) {
                    navigate("/assignments");
                  }
                } catch (error) {}
              }}
              onReject={() => {}}
              className={"btn btn-error btn-sm "}
              icon={<XCircleIcon className="h-4"></XCircleIcon>}
            ></ConfirmationButton>
          </div>
        )}
      </div>
    </form>
  );
}
